import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import type { ISchemaContainer } from './schemaContainer';
import { ServiceType } from '../enums/serviceType';
import { emailSchema } from './schemas/emailSchema';

export interface INotificationSettingsValidated {
    emailAddressOrganizationInbox: string | null;
    emailAddressOrganizationInboxAlt: string | null;
    sendNotificationOrganizationEmail: boolean;
    sendNotificationOrganizationInbox: boolean;
}

const serviceOrganizationSettingEmailNotificationEdit = Koi.object()
    .keys({
        emailAddressOrganizationInbox: emailSchema.allow(null),
        emailAddressOrganizationInboxAlt: emailSchema.allow(null),
        sendNotificationOrganizationEmail: Koi.boolean(),
        sendNotificationOrganizationInbox: Koi.boolean(),
    })
    .custom(evaluateEmailNotificationSettings);

export const serviceOrganizationSettingEmailNotificationAdd = Koi.object()
    .keys({
        emailAddressOrganizationInbox: Koi.when('service.serviceType', {
            is: Koi.valid(
                ServiceType.examination,
                ServiceType.consultation,
                ServiceType.referral,
                ServiceType.referralSmall
            ),
            otherwise: Koi.valid(null),
            then: emailSchema.allow(null),
        }),
        emailAddressOrganizationInboxAlt: Koi.when('service.serviceType', {
            is: Koi.valid(
                ServiceType.examination,
                ServiceType.consultation,
                ServiceType.referral,
                ServiceType.referralSmall
            ),
            otherwise: Koi.valid(null),
            then: emailSchema.allow(null),
        }),
        sendNotificationOrganizationEmail: Koi.when('sendNotificationOrganizationInbox', {
            is: true,
            otherwise: Koi.valid(false),
            then: Koi.boolean(),
        }),
        sendNotificationOrganizationInbox: Koi.when('service.serviceType', {
            is: Koi.valid(
                ServiceType.examination,
                ServiceType.consultation,
                ServiceType.referral,
                ServiceType.referralSmall
            ),
            otherwise: Koi.valid(false),
            then: Koi.boolean(),
        }),
    })
    .custom(evaluateEmailNotificationSettings);

function evaluateEmailNotificationSettings(
    notificationSettings: INotificationSettingsValidated,
    helpers: Joi.CustomHelpers
) {
    const {
        sendNotificationOrganizationInbox,
        sendNotificationOrganizationEmail,
        emailAddressOrganizationInbox,
        emailAddressOrganizationInboxAlt,
    } = notificationSettings;

    if (sendNotificationOrganizationInbox) {
        const emailAddressOrganizationInboxValidated = emailSchema.required().validate(emailAddressOrganizationInbox);
        const emailAddressOrganizationInboxAltValidated = emailSchema
            .required()
            .validate(emailAddressOrganizationInboxAlt);

        if (
            !sendNotificationOrganizationEmail &&
            emailAddressOrganizationInboxValidated.error &&
            emailAddressOrganizationInboxAltValidated.error
        ) {
            return helpers.error('email_required');
        }
    }

    return notificationSettings;
}

export const serviceOrganizationSettingEmailNotificationEditSchema: ISchemaContainer<INotificationSettingsValidated> = {
    schema: serviceOrganizationSettingEmailNotificationEdit.options({ convert: false, presence: 'required' }),
};
